/** TinyMCE default body size (12pt) maps to Malta preview body-content (0.66rem). */
export const TINYMCE_PREVIEW_BASE_PT = 12;
export const TINYMCE_PREVIEW_BASE_REM = 0.66;
export const TINYMCE_PREVIEW_BASE_PX = 16;
const PT_TO_REM_RATIO = TINYMCE_PREVIEW_BASE_REM / TINYMCE_PREVIEW_BASE_PT;
const PX_TO_REM_RATIO = TINYMCE_PREVIEW_BASE_REM / TINYMCE_PREVIEW_BASE_PX;

/** TinyMCE fontsize dropdown: 8pt 10pt 12pt 14pt 18pt 24pt 36pt */
export const TINYMCE_FONT_SIZE_PT_TO_REM: Record<number, string> = {
  8: "0.44rem",
  10: "0.55rem",
  12: "0.66rem",
  14: "0.77rem",
  18: "0.99rem",
  24: "1.32rem",
  36: "1.98rem",
};

const formatRemValue = (remValue: number) => `${remValue.toFixed(2).replace(/\.?0+$/, "")}rem`;

export const convertPtFontSizeToRem = (fontSize: string): string | null => {
  const ptMatch = fontSize.match(/([\d.]+)\s*pt/i);
  if (!ptMatch) return null;

  const ptValue = Number.parseFloat(ptMatch[1]);
  if (!Number.isFinite(ptValue)) return null;

  const mapped = TINYMCE_FONT_SIZE_PT_TO_REM[ptValue];
  if (mapped) return mapped;

  return formatRemValue(ptValue * PT_TO_REM_RATIO);
};

export const convertPxFontSizeToRem = (fontSize: string): string | null => {
  const pxMatch = fontSize.match(/([\d.]+)\s*px/i);
  if (!pxMatch) return null;

  const pxValue = Number.parseFloat(pxMatch[1]);
  if (!Number.isFinite(pxValue)) return null;

  const mappedPtEntry = Object.entries(TINYMCE_FONT_SIZE_PT_TO_REM).find(([pt]) => {
    const expectedPx = Number(pt) * (TINYMCE_PREVIEW_BASE_PX / TINYMCE_PREVIEW_BASE_PT);
    return Math.abs(pxValue - expectedPx) < 0.75;
  });
  if (mappedPtEntry) return mappedPtEntry[1];

  return formatRemValue(pxValue * PX_TO_REM_RATIO);
};

export const convertEditorFontSizeToRem = (fontSize: string): string | null =>
  convertPtFontSizeToRem(fontSize) ?? convertPxFontSizeToRem(fontSize);

const convertInlineFontSizesToRem = (root: HTMLElement) => {
  const elements: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>("[style]"))];

  elements.forEach((element) => {
    const styleAttr = element.getAttribute("style");
    if (!styleAttr || !/font-size/i.test(styleAttr)) return;

    const nextStyle = styleAttr.replace(/font-size\s*:\s*([^;]+)/gi, (match, rawValue: string) => {
      const remValue = convertEditorFontSizeToRem(rawValue.trim());
      return remValue ? `font-size: ${remValue}` : match;
    });

    if (nextStyle !== styleAttr) {
      element.setAttribute("style", nextStyle);
    }
  });
};

// --- simple parser array body for back
export const splitHtmlByBlocks = (html: string): string[] => {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html.trim();
  const blocks: string[] = [];

  div.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (["p", "ul", "ol"].includes(tag)) {
        blocks.push(el.outerHTML);
      }
    }
  });

  return blocks;
};

// --- parser array body for preview + convertation pt to rem
export const splitHtmlByBlocksWithFontConvert = (html: string): string[] => {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html.trim();
  const blocks: string[] = [];

  div.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (["p", "ul", "ol"].includes(tag)) {
        convertInlineFontSizesToRem(el);
        blocks.push(el.outerHTML);
      }
    }
  });

  return blocks;
};
